import React, { useState, useEffect, useContext } from 'react';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import { downloadCsv } from '../common/sharedFunctions';
import { ThemeContext } from '../components/Theme/ThemeContext';
import { getApp } from 'firebase/app';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

const CallLogs = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { isDark } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const columns = [
    { title: isRTL === 'rtl' ? 'رقم التسلسل' : 'Serial Number', field: 'serial', editable: 'never' },
    { title: isRTL === 'rtl' ? 'رقم المركبة' : 'Vehicle Plate', field: 'vehicleNumber', editable: 'never' },
    { title: isRTL === 'rtl' ? 'رقم المتصل (الماسح)' : 'Caller Phone', field: 'callerPhone', editable: 'never' },
    { 
      title: isRTL === 'rtl' ? 'وقت المكالمة' : 'Call Time', 
      field: 'timestamp', 
      editable: 'never', 
      defaultSort: 'desc', 
      render: rowData => rowData.timestamp ? moment(rowData.timestamp).format('lll') : (rowData.startTime ? moment(rowData.startTime).format('lll') : '-') 
    },
    { 
      title: isRTL === 'rtl' ? 'مدة المكالمة' : 'Call Duration', 
      field: 'callDurationText', 
      editable: 'never',
      render: rowData => {
        if (!rowData.callDurationText || rowData.callDurationText === '00:00') {
          return rowData.callDurationSecs ? `${rowData.callDurationSecs}s` : '00:00';
        }
        return rowData.callDurationText;
      }
    },
    { 
      title: isRTL === 'rtl' ? 'جهة الاتصال' : 'Target', 
      field: 'target', 
      editable: 'never',
      render: rowData => rowData.target === 'secondary' ? (isRTL === 'rtl' ? 'ثانوي' : 'Secondary') : (isRTL === 'rtl' ? 'رئيسي' : 'Primary')
    },
    { 
      title: isRTL === 'rtl' ? 'الحالة' : 'Status', 
      field: 'status', 
      editable: 'never',
      render: rowData => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold',
          backgroundColor: rowData.status === 'Completed' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(233, 174, 49, 0.15)',
          color: rowData.status === 'Completed' ? '#22c55e' : '#eab308',
          border: `1px solid ${rowData.status === 'Completed' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(233, 174, 49, 0.3)'}`
        }}>
          {rowData.status || 'Completed'}
        </span>
      )
    }
  ];

  useEffect(() => {
    const firestoreDb = getFirestore(getApp(), 'scanme');
    const callLogsRef = collection(firestoreDb, 'callLogs');

    const unsubscribe = onSnapshot(callLogsRef, (snapshot) => {
      const arr = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort newest first
      arr.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setData(arr);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching call logs:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    loading ? <CircularLoading /> :
      <div style={{ backgroundColor: 'var(--bg-main)', minHeight: '100vh', padding: '24px' }}>
        <MaterialTable
          title={isRTL === 'rtl' ? 'سجلات مكالمات المركبات' : 'Vehicle Call Logs'}
          columns={columns}
          style={{
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
            borderRadius: "20px",
            boxShadow: "var(--shadow)",
            padding: "0",
            backgroundColor: "var(--bg-card)",
            backdropFilter: "blur(12px)",
            border: "var(--card-border)",
            overflow: "hidden"
          }}
          data={data}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={{
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
            exportCsv: (columns, data) => {
              let hArray = [];
              const headerRow = columns.map(col => {
                hArray.push(col.field);
                return col.title;
              });
              const dataRows = data.map(({ tableData, ...row }) => {
                let dArr = [];
                for (let i = 0; i < hArray.length; i++) {
                  if (hArray[i] === 'timestamp') {
                    dArr.push(row.timestamp ? moment(row.timestamp).format('lll') : '');
                  } else {
                    dArr.push(row[hArray[i]] || '');
                  }
                }
                return Object.values(dArr);
              });
              const delimiter = ",";
              const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
              downloadCsv(csvContent, 'call_logs.csv');
            },
            exportButton: true,
            rowStyle: rowData => ({
              backgroundColor: (selectedRow === rowData.tableData.id) ? "var(--table-row-selected)" : "var(--table-row-bg)",
              borderBottom: "var(--table-border)",
              fontSize: "14px",
              color: "var(--text-main)"
            }),
            headerStyle: {
              backgroundColor: isDark ? '#1a1b23' : '#f8fafc',
              color: isDark ? '#ffffff' : '#0f172a',
              fontWeight: 'bold'
            }
          }}
        />
      </div>
  );
};

export default CallLogs;
